import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';

interface StaffLimitsData {
  currentCount: number;
  maxAllowed: number;
  canAddMore: boolean;
}

/**
 * Hook for HiveShare-only organizations to check staff user limits
 * Only runs for HiveShare users - Full Hivetivity users are unaffected
 */
export const useStaffLimits = () => {
  const { organizationId } = useAuth();
  const { isHiveShareOnly } = useSubscriptionAccess();

  return useQuery({
    queryKey: ['staffLimits', organizationId],
    queryFn: async (): Promise<StaffLimitsData> => {
      if (!organizationId) {
        throw new Error('No organization ID');
      }

      // Get current active staff count
      const { count: currentCount, error: countError } = await supabase
        .from('staff_members')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .eq('is_active', true);

      if (countError) throw countError;

      // Use database validation function for authoritative check
      const { data: canAddData, error: validateError } = await supabase
        .rpc('check_can_add_staff', { org_id: organizationId });

      if (validateError) throw validateError;

      return {
        currentCount: currentCount || 0,
        maxAllowed: 5, // HiveShare limit
        canAddMore: canAddData === true
      };
    },
    // CRITICAL: Only run for HiveShare users
    enabled: !!organizationId && isHiveShareOnly,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false // Avoid unnecessary calls
  });
};

/**
 * Simple validation hook - only for HiveShare users
 * Returns true if can add staff, false if at limit
 */
export const useCanAddStaff = () => {
  const { organizationId } = useAuth();
  const { isHiveShareOnly } = useSubscriptionAccess();

  return useQuery({
    queryKey: ['canAddStaff', organizationId],
    queryFn: async (): Promise<boolean> => {
      if (!organizationId) return false;

      const { data, error } = await supabase
        .rpc('check_can_add_staff', { org_id: organizationId });

      if (error) {
        console.error('Database validation error:', error);
        return false; // Fail safe
      }
      
      return data === true;
    },
    // CRITICAL: Only run for HiveShare users
    enabled: !!organizationId && isHiveShareOnly,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false
  });
};